import {
  CloseActionMethod,
  SendType,
} from "@redotech/redo-support-settings-common/support-settings-misc";
import {
  RingingStrategy,
  SupportVoiceCustomAudioType,
  VoiceProvider,
} from "@redotech/redo-support-settings-common/voice-settings";

interface SupportSettingSelectionOption<Value extends string = string> {
  value: Value;
  label: string;
  description?: string;
}

export function supportSettingOptionValues<
  const Options extends readonly SupportSettingSelectionOption[],
>(options: Options): readonly Options[number]["value"][] {
  return options.map((option) => option.value);
}

export const SUPPORT_DEFAULT_SEND_ACTION_OPTIONS = [
  {
    value: SendType.SEND,
    label: "Send message",
    description: "Leave the ticket in its current state after sending.",
  },
  {
    value: SendType.CLOSE,
    label: "Send and close ticket",
    description: "Close the ticket immediately after sending.",
  },
  {
    value: SendType.IN_PROGRESS,
    label: "Send and mark ticket in progress",
    description: "Move the ticket to in progress after sending.",
  },
] as const satisfies readonly SupportSettingSelectionOption<SendType>[];

export const SUPPORT_CLOSE_ACTION_OPTIONS = [
  { value: CloseActionMethod.NEXT, label: "Go to the next ticket" },
  { value: CloseActionMethod.TABLE, label: "Go back to the table" },
  { value: CloseActionMethod.STAY, label: "Stay on the ticket" },
] as const satisfies readonly SupportSettingSelectionOption<CloseActionMethod>[];

export const SUPPORT_TICKET_ASSIGNMENT_OPTIONS = [
  {
    value: "manual",
    label: "Manual",
    description: "Conversations are not automatically assigned.",
  },
  {
    value: "round-robin",
    label: "Round robin",
    description: "Assign conversations to teammates in sequential order.",
  },
  {
    value: "balanced",
    label: "Balanced",
    description:
      "Assign conversations to teammates with the fewest active conversations.",
  },
] as const satisfies readonly SupportSettingSelectionOption[];

export const SUPPORT_VOICE_PROVIDER_OPTIONS = [
  { value: VoiceProvider.TELNYX, label: "Telnyx" },
  { value: VoiceProvider.AIRCALL, label: "Aircall" },
] as const satisfies readonly SupportSettingSelectionOption<VoiceProvider>[];

export const SUPPORT_VOICE_RINGING_STRATEGY_OPTIONS = [
  {
    value: RingingStrategy.RING_TEAM,
    label: "Ring team",
    description: "Ring all available agents simultaneously.",
  },
  {
    value: RingingStrategy.ROUND_ROBIN,
    label: "Round robin",
    description: "Ring agents one at a time in rotation.",
  },
] as const satisfies readonly SupportSettingSelectionOption<RingingStrategy>[];

export const SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS = [
  { value: SupportVoiceCustomAudioType.TTS, label: "Text-to-speech" },
  { value: SupportVoiceCustomAudioType.URL, label: "Recorded audio" },
] as const satisfies readonly SupportSettingSelectionOption<SupportVoiceCustomAudioType>[];

export const SUPPORT_CONTACT_FORM_SUBJECT_INPUT_TYPE_OPTIONS = [
  { value: "dropdown", label: "Dropdown" },
  { value: "freeText", label: "Free text" },
] as const satisfies readonly SupportSettingSelectionOption[];

export const SUPPORT_CONTACT_FORM_REPLY_EMAIL_INTEGRATION_KIND_OPTIONS = [
  { value: "custom", label: "Custom email" },
  { value: "gmail", label: "Gmail" },
  { value: "outlook", label: "Outlook" },
] as const satisfies readonly SupportSettingSelectionOption[];

export const SUPPORT_CONTACT_FORM_FONT_OPTIONS = [
  { value: "inherit", label: "Inherit from storefront" },
  { value: "Arial", label: "Arial" },
  { value: "Helvetica", label: "Helvetica" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "Georgia", label: "Georgia" },
] as const satisfies readonly SupportSettingSelectionOption[];

export const SUPPORT_CSAT_SURVEY_SEND_TIME_OPTIONS = [
  "5 minutes",
  "30 minutes",
  "1 hour",
  "2 hours",
  "3 hours",
  "4 hours",
  "6 hours",
  "9 hours",
  "12 hours",
  "1 day",
  "2 days",
  "3 days",
  "7 days",
].map((value) => ({
  value,
  label: value,
})) satisfies readonly SupportSettingSelectionOption[];

export const SUPPORT_CSAT_SURVEY_METHOD_OPTIONS = [
  {
    value: "redoChat",
    label: "Chat",
    description: "Send the survey immediately after a chat ticket closes.",
  },
  { value: "email", label: "Email" },
] as const satisfies readonly SupportSettingSelectionOption[];

export const SUPPORT_TICKET_HANDOFF_TYPE_OPTIONS = [
  {
    value: "EMAIL",
    label: "Email",
    description: "Hand off by sending the conversation to an email inbox.",
  },
  {
    value: "LIVE_CHAT",
    label: "Live chat",
    description: "Hand off to the live chat support experience.",
  },
] as const satisfies readonly SupportSettingSelectionOption[];
